/* eslint-disable no-use-before-define */
import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import partition from 'lodash/partition';
import {
  deleteListById,
  getFavorites,
  getLists,
  getListsByVenue,
  getListBySlug,
  postFavorites,
  postCreateList,
  postAddVenueToList,
  getListVenues,
  deleteVenueFromList,
  patchVenueToList,
  postEditList,
  postGenerateShareableListLink,
  copyListBySlug,
} from '../api/api';

import {
  IDLE, PENDING, SUCCEEDED, FAILED,
} from '../constants/asyncLoadingStatuses';
import handlePayloadCreator from './helpers/handlePayloadCreator';

function transformVenues(data) {
  const venues = [];
  if (data) {
    data.map((val) => {
      const venue = val;
      if (typeof venue.id === 'number') return venues.push(venue);
      return venues.push({
        ...venue,
        averagePriceStr: venue.price_range_id,
        cuisine: venue.type,
        currencyDict: venue.currency,
        currency: venue.currency_symbol,
        gdaConciergeBooking: venue.gda_concierge_booking,
        id: venue.id.resy,
        images: venue.responsive_images,
        isGda: venue.is_global_dining_access,
        location: {
          locality: venue?.location?.locality,
          neighborhood: venue?.location?.neighborhood,
          urlSlug: venue?.location?.url_slug,
        },
        priceRange: venue.price_range_id,
        responsiveImages: venue.responsive_images,
        totalRatings: venue.rater[0].total,
        rating: (venue.rater[0]?.name === 'Resy') ? venue.rater[0]?.score : undefined,
        urlSlug: venue.url_slug,
        favorite: true,
        whyWeLikeIt: venue.content
          ? venue.content.find((item) => (
            item.name === 'why_we_like_it'
            && (item.body !== null && item.body !== '')
          )) : undefined,
      });
    });
  }
  return venues;
}

function transformVenue(data) {
  const templateId = data.venue.default_template;
  const template = data.templates[templateId];
  const images = template?.images;
  const { venue } = data;
  return {
    ...data,
    venue: {
      ...venue,
      averagePriceStr: venue.ticket.average_str,
      cuisine: venue.type,
      currencyDict: venue.currency,
      currency: venue.currency_symbol,
      gdaConciergeBooking: venue.gda_concierge_booking,
      id: venue.id.resy,
      images,
      isGda: venue.is_global_dining_access,
      priceRange: venue.price_range,
      responsiveImages: venue.responsive_images,
      totalRatings: venue.total_ratings,
      urlSlug: venue.url_slug,
    },
  };
}

// convenience method
export const toggleFavoriteVenue = ({ venueId, favorite }) => (dispatch) => {
  if (favorite) {
    dispatch(addFavorite(venueId));
  } else {
    dispatch(removeFavorite(venueId));
  }
};

export const getFavoriteVenues = (data) => (dispatch) => dispatch(getFavoritesThunk(data));

export const getMultipleLists = (data) => (dispatch) => dispatch(getListsThunk(data));

export const getMultipleListsByVenue = (venueId) => (dispatch) => dispatch(getListsByVenueThunk(venueId));

export const getMultipleListVenues = (data) => (dispatch) => dispatch(getListVenuesThunk(data));

export const getMultipleListVenuesBySlug = (data) => (dispatch) => dispatch(getListVenuesBySlugThunk(data));

export const removeVenueFromList = (data) => (dispatch) => dispatch(removeVenueFromListThunk(data));

export const addFavorite = (venueId) => async (dispatch) => {
  await dispatch(
    addFavoriteThunk({
      venue_id: venueId,
      favorite: 1,
    })
  );
  dispatch(getFavoritesThunk());
};

export const removeFavorite = (venueId) => async (dispatch) => {
  await dispatch(
    removeFavoriteThunk({
      venue_id: venueId,
      favorite: 0,
    })
  );
  dispatch(getFavoritesThunk());
};

const addFavoriteThunk = createAsyncThunk(
  'favorite/addFavorite',
  handlePayloadCreator(postFavorites)
);

const createListThunk = createAsyncThunk('favorite/createList', handlePayloadCreator(postCreateList), true);

const deleteListThunk = createAsyncThunk('favorite/deleteList', handlePayloadCreator(deleteListById), true);

const editListThunk = createAsyncThunk('favorite/editList', handlePayloadCreator(postEditList), true);

const patchVenueListsThunk = createAsyncThunk('favorite/patchVenueLists', handlePayloadCreator(patchVenueToList), true);

const generateShareableListLinkThunk = createAsyncThunk(
  'favorite/generateShareableListLink',
  handlePayloadCreator(postGenerateShareableListLink)
);

const addVenueToListThunk = createAsyncThunk(
  'favorite/addVenueToListThunk',
  postAddVenueToList
);

const getFavoritesThunk = createAsyncThunk(
  'favorite/getFavorites',
  handlePayloadCreator(getFavorites)
);

const removeFavoriteThunk = createAsyncThunk(
  'favorite/removeFavorite',
  handlePayloadCreator(postFavorites)
);

const removeVenueFromListThunk = createAsyncThunk(
  'favorite/removeVenueFromList',
  deleteVenueFromList
);

const getListsThunk = createAsyncThunk('favorite/getLists', handlePayloadCreator(getLists));

const getListsByVenueThunk = createAsyncThunk('favorite/getListsByVenue', handlePayloadCreator(getListsByVenue));

const getListVenuesThunk = createAsyncThunk(
  'favorite/getListVenues',
  handlePayloadCreator(getListVenues)
);

const getListVenuesBySlugThunk = createAsyncThunk(
  'favorite/getListVenuesBySlug', handlePayloadCreator(getListBySlug)
);

const getListBySlugThunk = createAsyncThunk('favorite/getListBySlug', handlePayloadCreator(getListBySlug));

const copyListBySlugThunk = createAsyncThunk('favorite/copyListBySlug', handlePayloadCreator(copyListBySlug));

function handleUpdateResponse(state, response) {
  state.venueIds = response.payload?.venues;
}

function handleGetResponse(state, response) {
  state.loaded = true;
  state.venues = response.payload?.results?.venues.map(transformVenue);
  state.venueIds = state.venues?.map((data) => data.venue.id);
}

function handleErrorResponse(state, response) {
  state.listsStatus.error = {
    ...state.listsStatus.error,
    ...response.payload,
  };
  state.isLoading = false;
}

function handleCreateListResponse(state, response) {
  const newlyCreatedList = response.payload;

  state.isLoading = false;

  const [myHitListArray, remainingListItems] = partition(state.lists, (list) => list.isLegacyHitlist);

  const sanitizedListItems = [...(myHitListArray || []), newlyCreatedList, ...(remainingListItems || [])].filter(Boolean);
  state.lists = getNormalizedList(sanitizedListItems);

  /* Enables auto selection of the newly created list when
  /* the modal switches back to ADD_TO_LIST mode.
  */
  state.newlyCreatedList = state.lists.find((list) => list.listId === newlyCreatedList?.list_id);

  if (state.multiStepModalVenueId) {
    state.multiStepModalMode = MODAL_MODES.ADD_TO_LIST;
  } else {
    state.isMultiStepModalOpen = false;
  }
}

function handleDeleteListResponse(state) {
  state.isLoading = false;
  state.isMultiStepModalOpen = false;
  window.history.replaceState({}, '', 'account/lists');
}

function handleEditListResponse(state, response) {
  state.isLoading = false;
  state.listById[response.meta?.arg?.list_id].list = response.payload;
  state.isMultiStepModalOpen = false;
}

function handleGenerateShareableListLinkResponse(state, response) {
  state.isLoading = false;
  state.shareableListLinkDetails = {
    ...(state.shareableListLinkDetails || {}),
    urlSlug: response.payload?.url_slug,
  };
}

function handleGenerateShareableListLinkReject(state, response) {
  state.isLoading = false;
  state.shareableListLinkDetails = {
    ...(state.shareableListLinkDetails || {}),
    error: new Error(response.payload),
  };
}

function handleAddVenueToListResponse(state) {
  state.isLoading = false;
}

function handlePatchVenueListsResponse(state) {
  state.isMultiStepModalOpen = false;
  state.isLoading = false;
}

const getNormalizedList = (lists) => {
  const normalizedList = lists?.reduce((acc, list) => {
    acc.push({
      name: list.name,
      count: list.count,
      description: list.description,
      isLegacyHitlist: list.is_legacy_hitlist || list.isLegacyHitlist,
      listId: list.list_id || list.listId,
      responsiveImages: list.responsive_images || list.responsiveImages,
      urlSlug: list.url_slug || list.urlSlug,
      isVenueOnList: list.is_venue_on_list || list.isVenueOnList,
      createdBy: list.created_by,
    });

    return acc;
  }, []);

  return normalizedList;
};

function handleListsResponse(state, response) {
  const lists = response.payload?.results?.lists;

  const normalizedList = getNormalizedList(lists);

  state.listsStatus.loaded = true;
  state.lists = normalizedList;
  state.isLoading = false;
}
const MODAL_MODES = {
  ADD_TO_LIST: 'ADD_TO_LIST',
  COPY_LIST: 'COPY_LIST',
  CREATE_LIST: 'CREATE_LIST',
  EDIT_LIST: 'EDIT_LIST',
  DELETE_LIST: 'DELETE_LIST',
  HIT_LIST: 'HIT_LIST',
};

function handleListVenuesResponse(state, response) {
  const listById = current(state).listById[
    response.meta?.arg?.listId || response.meta?.arg?.slug
  ];
  const { results: { venues: incomingVenues } } = response?.payload;
  let venues;

  if (listById) {
    const { results: { venues: currentVenues } } = listById;
    const currentVenueIds = currentVenues.map((venue) => (venue.id));
    const incomingVenueIds = incomingVenues.map((venue) => (venue.id?.resy));
    const uniqueVenues = (currentVenueIds.length > 0)
      ? currentVenues.map((venue) => ({
        ...venue,
        favorite: incomingVenueIds.includes(venue.id),
      })) : [];
    incomingVenues.map((venue) => {
      if (!currentVenueIds.includes(venue.id?.resy)) {
        uniqueVenues.push({
          ...venue,
          favorite: true,
        });
      }
      return true;
    });
    venues = transformVenues(uniqueVenues);
  } else {
    venues = transformVenues(incomingVenues);
  }

  state.listById = {
    [response.meta?.arg?.listId || response.meta?.arg?.slug]: {
      ...response?.payload,
      results: {
        venues,
      },
    } || null,
  };
}

const defaultState = {
  loaded: false,
  listsStatus: {
    loaded: false,
    error: {},
  },
  isLoading: false,
  venues: [],
  venueIds: [],
  lists: [],
  multiStepModalVenueId: undefined,
  multiStepModalVenueContext: undefined,
  multiStepModalMode: MODAL_MODES.ADD_TO_LIST,
  isMultiStepModalOpen: false,
  newlyCreatedList: undefined,
  listById: {},
  listByShareSlug: {
    status: IDLE,
    results: undefined,
  },
  count: 0,
};

const favoriteSlice = createSlice({
  name: 'favorite',
  initialState: defaultState,
  reducers: {
    resetFavorites: () => defaultState,
    setMultiStepModalMode: (state, action) => {
      state.multiStepModalMode = action.payload;
    },
    setMultiStepModalOpenState: (state, action) => {
      state.isMultiStepModalOpen = action.payload;
    },
    setMultiStepModalVenueId: (state, action) => {
      state.multiStepModalVenueId = action.payload;
    },
    setMultiStepModalVenueContext: (state, action) => {
      state.multiStepModalVenueContext = action.payload;
    },
    resetListById: (state) => {
      state.listById = {};
    },
    setErrorModalOpenState: (state, action) => {
      state.listsStatus.error.isErrorModalOpen = action.payload;
    },
    resetModalErrorState: (state) => {
      state.listsStatus.error.isErrorModalOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      addFavoriteThunk.fulfilled,
      (state, action) => handleUpdateResponse(state, action)
    );
    builder.addCase(
      addFavoriteThunk.rejected,
      (state, action) => handleUpdateResponse(state, action)
    );
    builder.addCase(
      removeFavoriteThunk.fulfilled,
      (state, action) => handleUpdateResponse(state, action)
    );
    builder.addCase(
      removeFavoriteThunk.rejected,
      (state, action) => handleUpdateResponse(state, action)
    );
    builder.addCase(
      getFavoritesThunk.fulfilled,
      (state, action) => handleGetResponse(state, action)
    );
    builder.addCase(
      getFavoritesThunk.rejected,
      (state, action) => handleGetResponse(state, action)
    );
    builder.addCase(
      getListsThunk.fulfilled,
      (state, action) => handleListsResponse(state, action)
    );
    builder.addCase(
      getListsThunk.rejected,
      (state, action) => handleListsResponse(state, action)
    );
    builder.addCase(
      getListsByVenueThunk.fulfilled,
      (state, action) => handleListsResponse(state, action)
    );
    builder.addCase(
      getListsByVenueThunk.rejected,
      (state, action) => handleListsResponse(state, action)
    );
    builder.addCase(
      createListThunk.fulfilled,
      (state, action) => handleCreateListResponse(state, action)
    );
    builder.addCase(
      createListThunk.rejected,
      (state, action) => handleErrorResponse(state, action)
    );
    builder.addCase(createListThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      deleteListThunk.fulfilled,
      (state, action) => handleDeleteListResponse(state, action)
    );
    builder.addCase(
      deleteListThunk.rejected,
      (state, action) => handleErrorResponse(state, action)
    );
    builder.addCase(deleteListThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      editListThunk.fulfilled,
      (state, action) => handleEditListResponse(state, action)
    );
    builder.addCase(
      editListThunk.rejected,
      (state, action) => handleErrorResponse(state, action)
    );
    builder.addCase(editListThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      generateShareableListLinkThunk.fulfilled,
      (state, action) => handleGenerateShareableListLinkResponse(state, action)
    );
    builder.addCase(
      generateShareableListLinkThunk.rejected,
      (state, action) => handleGenerateShareableListLinkReject(state, action)
    );
    builder.addCase(generateShareableListLinkThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      addVenueToListThunk.fulfilled,
      (state, action) => handleAddVenueToListResponse(state, action)
    );
    builder.addCase(
      addVenueToListThunk.rejected,
      (state, action) => handleAddVenueToListResponse(state, action)
    );
    builder.addCase(addVenueToListThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getListVenuesThunk.fulfilled,
      (state, action) => handleListVenuesResponse(state, action));
    builder.addCase(getListVenuesThunk.rejected,
      (state, action) => handleListVenuesResponse(state, action));
    builder.addCase(getListVenuesBySlugThunk.fulfilled,
      (state, action) => handleListVenuesResponse(state, action));
    builder.addCase(getListVenuesBySlugThunk.rejected,
      (state, action) => handleListVenuesResponse(state, action));
    builder.addCase(
      patchVenueListsThunk.fulfilled,
      (state, action) => handlePatchVenueListsResponse(state, action)
    );
    builder.addCase(
      patchVenueListsThunk.rejected,
      (state, action) => handleErrorResponse(state, action)
    );
    builder.addCase(patchVenueListsThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getListBySlugThunk.fulfilled, (state, action) => {
      if (!action.payload?.metadata) {
        state.listByShareSlug.results = {
          results: {
            venues: null,
          },
        };
      } else {
        const currentState = current(state);

        const { offset, limit } = action.payload.metadata;

        if (offset === 0) {
          state.listByShareSlug.results = {
            ...action.payload,
            results: { venues: transformVenues(action.payload.results.venues) },
          };
        } else if (currentState.listByShareSlug.results) {
          const { offset: currentOffset } = currentState.listByShareSlug.results?.metadata || null;
          if (offset === (currentOffset + limit)) {
            const { venues } = currentState.listByShareSlug.results.results;
            state.listByShareSlug.results = {
              ...action.payload,
              results: {
                venues: venues.concat(transformVenues(action.payload.results.venues)),
              },
            };
          }
        }
      }

      state.listByShareSlug.status = SUCCEEDED;
    });
    builder.addCase(getListBySlugThunk.rejected, (state, action) => {
      state.listByShareSlug.results = {
        ...action.payload,
        results: {
          venues: null,
        },
      };
      state.listByShareSlug.status = FAILED;
    });
    builder.addCase(getListBySlugThunk.pending, (state) => {
      state.listByShareSlug.status = PENDING;
    });
    builder.addCase(copyListBySlugThunk.fulfilled, (state) => {
      state.isLoading = false;
      state.isMultiStepModalOpen = false;
      window.history.replaceState({}, '', 'account/lists');
    });
    builder.addCase(copyListBySlugThunk.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(copyListBySlugThunk.pending, (state) => {
      state.isLoading = true;
    });
  },
});

export {
  addVenueToListThunk,
  copyListBySlugThunk,
  createListThunk,
  deleteListThunk,
  editListThunk,
  generateShareableListLinkThunk,
  getListBySlugThunk,
  MODAL_MODES,
  patchVenueListsThunk,
};

export const {
  resetModalErrorState,
  resetFavorites,
  resetListById,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setErrorModalOpenState,
} = favoriteSlice.actions;

export default favoriteSlice.reducer;
